/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useEffect, useState } from "react";
import { v4 as uuid } from "uuid";
import { Card } from "@material-ui/core";
import { StatusAlertsProvider, useStatusAlerts } from "../context/statusAlerts";
import {
	ContentLayout,
	LeftPanelLayout,
	PanelContainer,
	ProgressOverlay,
	RightPanelLayout,
	ShowSnackbar,
} from "../../../components/Common";
import StatusList from "../components/AgentStatus/StatusList";
import { AgentStatusSettings } from "../components/AgentStatus/AgentStatusSettings";

const validateStatusAlert = (statusAlerts) => {
	if (!Array.isArray(statusAlerts)) {
		return false;
	}
	let response = true;
	for (let statusIndex = 0; statusIndex < statusAlerts.length; statusIndex++) {
		const status = statusAlerts[statusIndex];
		if (!status.statusId || !status.statusName) {
			response = false;
			break;
		}
		for (let alertIndex = 0; alertIndex < status.alerts.length; alertIndex++) {
			const alert = status?.alerts[alertIndex];
			if (!alert.time || !alert.message || alert.error) {
				response = false;
				break;
			}
		}
	}
	return response;
};

const checkForChanges = (statusAlerts) => {
	return statusAlerts?.find((statusAlert) => "activity" in statusAlert) !== undefined;
};

/**
 * This page allows the user to edit alerts that an agent will see if they are on a certain status for a set amount of time.
 */
const StatusAlertsPageContent = () => {
	const [notification, setNotification] = React.useState({ message: "", severity: "success" });
	const [filteredStatuses, setFilteredStatuses] = useState([]);
	const [selectedStatusId, setSelectedStatusId] = useState("");

	const {
		addStatusAlert,
		statusAlerts,
		loading,
		error,
		clearStatusAlerts,
		message,
		getAgentStatusList,
		getStatusAlertsList,
	} = useStatusAlerts();

	const handleAddStatusAlert = () => {
		//Creating new status from template
		const newStatusAlert = {
			id: uuid(),
			alerts: [{ id: uuid(), time: "", message: "" }],
			statusName: "",
			active: true,
			new: true,
			activity: "NEW",
		};
		addStatusAlert(newStatusAlert);
		setSelectedStatusId(newStatusAlert.id);
	};

	useEffect(() => {
		const fetchData = async () => {
			await getStatusAlertsList();
			await getAgentStatusList();
		};
		fetchData();
	}, [getAgentStatusList, getStatusAlertsList]);

	const handleSnackBarClose = () => {
		setNotification((cur) => ({ ...cur, message: "" }));
	};

	const handleCancel = () => {
		clearStatusAlerts();
		setSelectedStatusId("");
	};

	useEffect(() => {
		if (loading) {
			setNotification({ message: "", severity: "success" });
		} else if (error) {
			setNotification({ message: error, severity: "error" });
		} else {
			setNotification({ message, severity: "success" });
		}
	}, [loading, error, message]);

	const validationState = validateStatusAlert(statusAlerts);
	const isFormChanged = checkForChanges(statusAlerts);
	const statusAlertList = statusAlerts.filter((alt) => !!alt.statusName);

	return (
		<ContentLayout>
			<ShowSnackbar message={notification.message} onClose={handleSnackBarClose} severity={notification.severity} />
			{loading && <ProgressOverlay />}
			<PanelContainer>
				<LeftPanelLayout>
					<Card style={{ minHeight: 600, height: "100%", padding: "10px 5px" }}>
						<StatusList
							statusAlerts={statusAlertList} // excluding newly added one.
							clearStatusAlerts={clearStatusAlerts}
							addStatusAlertHandler={handleAddStatusAlert}
							selectStatusAlertIdHandler={setSelectedStatusId}
							selectedStatusAlertId={selectedStatusId}
							formEdited={isFormChanged}
						/>
					</Card>
				</LeftPanelLayout>
				<RightPanelLayout>
					<Card>
						<AgentStatusSettings
							selectedStatusId={selectedStatusId}
							setSelectedStatusId={setSelectedStatusId}
							isFormChanged={isFormChanged}
							searchStatuses={filteredStatuses}
							setFilteredStatuses={setFilteredStatuses}
							validationState={validationState}
							onCancel={handleCancel}
						/>
					</Card>
				</RightPanelLayout>
			</PanelContainer>
		</ContentLayout>
	);
};

const StatusAlertsManagementPage = (props) => (
	<StatusAlertsProvider>
		<StatusAlertsPageContent {...props} />
	</StatusAlertsProvider>
);

export default StatusAlertsManagementPage;
